import "./style.css";
import { detectHost, getIntegrationEndpoints } from "@repo/api";
import type { IntegrationTypeCategory } from "./hooks/useIntegrationTypes";
import { isManagementFlowType } from "./utils/integrationIcons";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AddIntegrationTypeModal } from "./components/AddIntegrationTypeModal";
import IntegrationModal from "./components/IntegrationModal";
import { RoutingProvider } from "./contexts/RoutingContext";
import { Integrations } from "./pages/Integrations";

type AlertState = {
  type: "success" | "error" | "draft";
  message: string;
};

type ModalMode = "closed" | "new" | "edit";

/**
 * Main App component for Integrations MFE.
 *
 * Under the ispm host, modal open/close is driven by URL:
 *   /integrations/           → list
 *   /integrations/new?type=X → create-integration form
 *   /integrations/edit?id=X&type=Y → edit form
 *
 * Under legacy / standalone, falls back to local state (the MFE's own
 * `__root.tsx` + `IntegrationsShell` is used in those flows).
 */
export default function App() {
  const host = detectHost();
  const isIspmHost = host === "ispm";

  return isIspmHost ? <AppIspmHost /> : <AppLocalState />;
}

// ─── ispm host: URL-driven ────────────────────────────────────────────────
function AppIspmHost() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [addCategory, setAddCategory] = useState<
    IntegrationTypeCategory | undefined
  >(undefined);

  const basepath = getIntegrationEndpoints().routes.basepath;

  const { mode, initialType, editId } = useMemo<{
    mode: ModalMode;
    initialType?: string;
    editId?: string;
  }>(() => {
    const pathname = routerState.location.pathname;
    const search = routerState.location.search as {
      type?: string;
      id?: string;
    };
    if (pathname === `${basepath}/new` || pathname.endsWith("/new")) {
      return { mode: "new", initialType: search.type };
    }
    if (pathname === `${basepath}/edit` || pathname.endsWith("/edit")) {
      return { mode: "edit", initialType: search.type, editId: search.id };
    }
    return { mode: "closed" };
  }, [routerState.location.pathname, routerState.location.search, basepath]);

  const goToIntegrations = () =>
    navigate({ to: basepath, replace: false, state: {} });

  const handleAddIntegration = (category?: IntegrationTypeCategory) => {
    setAlert(null);
    setAddCategory(category);
    setShowTypeSelector(true);
  };

  const handleSelectType = (typeName: string) => {
    setShowTypeSelector(false);
    navigate({
      to: `${basepath}/new`,
      search: { type: typeName },
    });
  };

  const handleEditIntegration = (
    integrationId: string,
    integrationType?: string,
  ) => {
    setAlert(null);
    const id = String(integrationId).replace(/^"|"$/g, "").trim();
    if (isManagementFlowType(integrationType ?? "")) {
      // Full page reload required — see "Cross-MFE Navigation" in README.md (TD-001)
      window.location.assign(
        `${basepath}/universalOnboarding/identitySources/edit?id=${id}`,
      );
      return;
    }
    navigate({
      to: `${basepath}/edit`,
      search: {
        id,
        type: integrationType?.trim() || "AWS",
      },
    });
  };

  const handleCloseModal = (closeAlert?: AlertState) => {
    if (closeAlert) setAlert(closeAlert);
    goToIntegrations();
  };

  return (
    <RoutingProvider
      handleAddIntegration={handleAddIntegration}
      handleEditIntegration={handleEditIntegration}
      isStandaloneMode={false}
    >
      <Integrations
        onAddIntegration={handleAddIntegration}
        alert={alert}
        onAlertDismiss={() => setAlert(null)}
      />
      <AddIntegrationTypeModal
        isOpen={showTypeSelector}
        onClose={() => setShowTypeSelector(false)}
        onSelectType={handleSelectType}
        category={addCategory}
      />
      <IntegrationModal
        isOpen={mode !== "closed"}
        onClose={handleCloseModal}
        integrationId={mode === "edit" ? editId : undefined}
        initialSelectedType={initialType}
      />
    </RoutingProvider>
  );
}

// ─── Non-ispm (legacy direct-import dev path, standalone): local state ────
function AppLocalState() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIntegrationId, setEditIntegrationId] = useState<
    string | undefined
  >(undefined);
  const [alert, setAlert] = useState<AlertState | null>(null);

  const isStandaloneMode = detectHost() === "standalone";

  const handleAddIntegration = () => {
    setAlert(null);
    setEditIntegrationId(undefined);
    setIsModalOpen(true);
  };

  const handleEditIntegration = (
    integrationId: string,
    integrationType?: string,
  ) => {
    setAlert(null);
    if (isManagementFlowType(integrationType ?? "")) {
      // Full page reload required — see "Cross-MFE Navigation" in README.md (TD-001)
      window.location.assign(
        `/integrations/universalOnboarding/identitySources/edit?id=${integrationId}`,
      );
      return;
    }
    setEditIntegrationId(integrationId);
    setIsModalOpen(true);
  };

  const handleCloseModal = (closeAlert?: AlertState) => {
    setIsModalOpen(false);
    setEditIntegrationId(undefined);
    if (closeAlert) setAlert(closeAlert);
  };

  return (
    <RoutingProvider
      handleAddIntegration={handleAddIntegration}
      handleEditIntegration={handleEditIntegration}
      isStandaloneMode={isStandaloneMode}
    >
      <Integrations
        onAddIntegration={handleAddIntegration}
        alert={alert}
        onAlertDismiss={() => setAlert(null)}
      />
      <IntegrationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        integrationId={editIntegrationId}
      />
    </RoutingProvider>
  );
}
